import { useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TaskDetailDTO, WorkflowDTO } from "@app-factory/shared";
import { api, ApiRequestError } from "@/lib/api";
import { cs } from "@/i18n/cs";
import {
  Btn,
  Chip,
  DetailHeader,
  EmptyState,
  Panel,
  inputClass,
  toneColor,
  type Tone,
} from "@/design/primitives";
import { relativeTime, sessionCostLabel } from "@/lib/format";
import {
  canContribute,
  canManage,
  useProjectRole,
} from "../projects/project-data";

const actorTone: Record<string, Tone> = {
  HUMAN: "amber",
  AI: "cyan",
  WORKER: "green",
  SYSTEM: "dim",
};

export function TaskDetailPage() {
  const params = useParams({ strict: false }) as {
    slug: string;
    number: string;
  };
  const projectQuery = useQuery({
    queryKey: ["projects", params.slug],
    queryFn: () => api.project(params.slug),
  });
  const taskQuery = useQuery({
    queryKey: ["task", params.slug, params.number],
    queryFn: () => api.task(params.slug, Number(params.number)),
    retry: (count, err) =>
      !(err instanceof ApiRequestError && err.status === 404) && count < 2,
  });
  const workflows = useQuery({
    queryKey: ["workflows"],
    queryFn: api.workflows,
  });

  if (taskQuery.error instanceof ApiRequestError) {
    return (
      <Panel className="mx-auto mt-16 max-w-md p-6 text-center">
        <EmptyState title={cs.task.notFound} />
        <Link
          to="/projects/$slug/$tab"
          params={{ slug: params.slug, tab: "kanban" }}
          className="text-[12px]"
        >
          {cs.task.backToKanban(params.slug)}
        </Link>
      </Panel>
    );
  }

  const task = taskQuery.data?.task;
  const project = projectQuery.data?.project;
  if (!task || !project) return null;

  return (
    <TaskDetail
      task={task}
      projectId={project.id}
      workflow={findWorkflow(workflows.data?.workflows ?? [], task)}
      slug={params.slug}
    />
  );
}

/** Workflow tasku: podle klíče oddělení (dev/qa/simple) — hledáme podle stavů. */
function findWorkflow(
  workflows: WorkflowDTO[],
  task: TaskDetailDTO,
): WorkflowDTO | null {
  const byState = workflows.filter((w) =>
    w.states.some((s) => s.key === task.status),
  );
  if (byState.length === 1) return byState[0]!;
  // víc kandidátů (DONE má každé workflow) — preferuj podle oddělení
  const deptDefault =
    task.departmentKey === "development"
      ? "development"
      : task.departmentKey === "qa"
        ? "qa"
        : "simple";
  return workflows.find((w) => w.key === deptDefault) ?? byState[0] ?? null;
}

function TaskDetail({
  task,
  projectId,
  workflow,
  slug,
}: {
  task: TaskDetailDTO;
  projectId: string;
  workflow: WorkflowDTO | null;
  slug: string;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const projectQuery = useQuery({
    queryKey: ["projects", slug],
    queryFn: () => api.project(slug),
  });
  const role = useProjectRole(
    projectQuery.data?.project ?? ({ members: [] } as never),
  );
  const manager = canManage(role);
  const contributor = canContribute(role);
  const [comment, setComment] = useState("");
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: ["task", slug, String(task.number)],
    });
    void queryClient.invalidateQueries({
      queryKey: ["section", projectId, "tasks"],
    });
  };
  const onError = (err: unknown) =>
    setError(err instanceof Error ? err.message : cs.common.error);

  const moveMutation = useMutation({
    mutationFn: (status: string) => api.moveTask(task.id, { status }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError,
  });
  const pinMutation = useMutation({
    mutationFn: () =>
      task.pinnedAt ? api.unpinTask(task.id) : api.pinTask(task.id),
    onSuccess: invalidate,
    onError,
  });
  const acMutation = useMutation({
    mutationFn: (acceptance: TaskDetailDTO["acceptance"]) =>
      api.updateTask(task.id, { acceptance }),
    onSuccess: invalidate,
    onError,
  });
  const attachMutation = useMutation({
    mutationFn: (file: File) => api.addTaskAttachment(slug, task.number, file),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError,
  });
  const commentMutation = useMutation({
    mutationFn: () => api.addTaskComment(task.id, comment),
    onSuccess: () => {
      setComment("");
      invalidate();
    },
    onError,
  });

  const state = workflow?.states.find((s) => s.key === task.status);
  const stateTone = (state?.tone as Tone) ?? "cyan";
  const availableMoves = (workflow?.transitions ?? []).filter(
    (t) => t.from === task.status,
  );
  const pauseTarget = availableMoves.find((t) => t.kind === "RETURN");
  const stopTarget = workflow?.states.find((s) => s.kind === "FAILED");

  return (
    <div className="af-animate-fade">
      <DetailHeader
        back={
          <button
            type="button"
            className="cursor-pointer"
            style={{ color: "inherit" }}
            onClick={() =>
              void navigate({
                to: "/projects/$slug/$tab",
                params: { slug, tab: "kanban" },
              })
            }
          >
            {cs.task.backToKanban(task.projectName)}
          </button>
        }
        title={`#${task.number} ${task.title}`}
        right={
          <>
            <span data-testid="task-status">
              <Chip tone={stateTone}>{state?.label ?? task.status}</Chip>
            </span>
            {task.departmentName ? (
              <span
                className="af-mono px-3 py-[3px] text-[10px]"
                style={{
                  borderRadius: "var(--radius-pill)",
                  border: "1px solid var(--border-default)",
                  color: "var(--text-tertiary)",
                }}
              >
                {task.departmentName.toUpperCase()}
              </span>
            ) : null}
            <span
              className="af-mono text-[11px]"
              style={{ color: "var(--warning)" }}
            >
              {task.priority} → EP {task.epScore}
            </span>
            {manager ? (
              <Btn
                variant={task.pinnedAt ? "secondary" : "danger"}
                tone="amber"
                onClick={() => pinMutation.mutate()}
                data-testid="task-pin"
              >
                {task.pinnedAt ? cs.task.undoNext : cs.task.doNext}
              </Btn>
            ) : null}
            {contributor && pauseTarget ? (
              <Btn
                onClick={() => moveMutation.mutate(pauseTarget.to)}
                title={`→ ${pauseTarget.to}`}
              >
                {cs.task.pause}
              </Btn>
            ) : null}
            {manager && stopTarget && task.status !== stopTarget.key ? (
              <Btn
                variant="danger"
                onClick={() => {
                  if (confirm(`Zrušit task #${task.number}?`))
                    moveMutation.mutate(stopTarget.key);
                }}
              >
                {cs.task.stop}
              </Btn>
            ) : null}
          </>
        }
      />

      {error ? (
        <div
          className="mt-2 text-[12px]"
          style={{ color: "var(--critical)" }}
          data-testid="task-error"
        >
          ⚠ {error}
        </div>
      ) : null}

      <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-[1.25fr_1fr]">
        {/* levý sloupec */}
        <div className="flex flex-col gap-4">
          <Panel className="p-4">
            <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
              {cs.task.description}
            </div>
            <div
              className="text-[11px] leading-relaxed"
              style={{ color: "var(--text-primary)" }}
            >
              {task.description || "—"}
            </div>
            {task.attachments.length > 0 ? (
              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {task.attachments.map((a) => {
                  const url = api.taskAttachmentImageUrl(
                    task.projectSlug,
                    task.number,
                    a.id,
                  );
                  return (
                    <a
                      key={a.id}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      title={a.note ?? a.filename}
                      data-testid={`task-attachment-${a.id}`}
                    >
                      <img
                        src={url}
                        alt={a.note ?? a.filename}
                        loading="lazy"
                        className="block h-[110px] w-full object-cover object-top"
                        style={{
                          borderRadius: "var(--radius-box)",
                          border: "1px solid var(--border-subtle)",
                          background: "var(--surface-inset)",
                        }}
                      />
                      <div
                        className="mt-1 truncate text-[11px]"
                        style={{ color: "var(--text-tertiary)" }}
                      >
                        {a.note ?? a.filename}
                      </div>
                    </a>
                  );
                })}
              </div>
            ) : null}
            {contributor ? (
              <AttachmentDrop
                busy={attachMutation.isPending}
                full={task.attachments.length >= 10}
                onFile={(file) => attachMutation.mutate(file)}
              />
            ) : null}
            <div className="mt-2.5 flex flex-wrap gap-2">
              {task.milestoneTitle ? (
                <MetaChip label={`milestone: ${task.milestoneTitle}`} />
              ) : null}
              {task.applications.map((a) => (
                <MetaChip key={a.id} label={`app: ${a.slug}`} />
              ))}
              {task.codebases.map((c) => (
                <MetaChip key={c.id} label={`repo: ${c.slug}`} />
              ))}
              {task.deadline ? (
                <MetaChip
                  label={`deadline: ${new Date(task.deadline).toLocaleDateString("cs-CZ")}`}
                />
              ) : null}
              {task.origin !== "SCOPE" ? (
                <MetaChip label={`origin: ${task.origin}`} />
              ) : null}
            </div>
          </Panel>

          <Panel className="p-4">
            <div className="af-label mb-2.5" style={{ color: "var(--accent)" }}>
              {cs.task.acceptance}
            </div>
            {task.acceptance.length === 0 ? (
              <div className="text-[12px]" style={{ color: "var(--warning)" }}>
                ⚠ Bez acceptance criteria nejde task do READY (spec §27).
              </div>
            ) : (
              task.acceptance.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  disabled={!contributor}
                  className="mb-1.5 flex w-full cursor-pointer gap-2.5 text-left text-[12px] leading-snug"
                  onClick={() =>
                    acMutation.mutate(
                      task.acceptance.map((x) =>
                        x.id === a.id ? { ...x, met: !x.met } : x,
                      ),
                    )
                  }
                  data-testid="task-ac"
                >
                  <span
                    style={{
                      color: a.met ? "var(--positive)" : "var(--text-tertiary)",
                    }}
                  >
                    {a.met ? "✓" : "□"}
                  </span>
                  <span
                    style={{
                      color: "var(--text-primary)",
                      textDecoration: a.met ? "line-through" : undefined,
                    }}
                  >
                    {a.text}
                  </span>
                </button>
              ))
            )}
          </Panel>

          <Panel className="p-4">
            <div className="flex justify-between">
              <span className="af-label" style={{ color: "var(--accent)" }}>
                {cs.task.planTitle}
              </span>
              <span className="af-label-dim">{cs.task.planNote}</span>
            </div>
            <div className="mt-2">
              {task.plan.length === 0 ? (
                <div
                  className="text-[12px]"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  {cs.task.planEmpty}
                </div>
              ) : (
                task.plan.map((p, i) => (
                  <div
                    key={i}
                    className="mb-1.5 flex gap-2.5 font-mono text-[12px]"
                  >
                    <span
                      style={{
                        color:
                          p.state === "DONE"
                            ? "var(--positive)"
                            : p.state === "DOING"
                              ? "var(--accent)"
                              : "var(--text-tertiary)",
                      }}
                    >
                      {p.state === "DONE"
                        ? "[x]"
                        : p.state === "DOING"
                          ? "[>]"
                          : "[ ]"}
                    </span>
                    <span
                      style={{
                        color:
                          p.state === "DONE"
                            ? "var(--text-secondary)"
                            : "var(--text-secondary)",
                      }}
                    >
                      {p.text}
                    </span>
                  </div>
                ))
              )}
            </div>
          </Panel>

          <Panel className="p-4">
            <div className="af-label mb-3" style={{ color: "var(--accent)" }}>
              {cs.task.discussion}
            </div>
            <div className="flex flex-col gap-2.5">
              {task.discussion.map((m) => (
                <div key={m.id} className="flex gap-3">
                  <div className="w-20 flex-none text-right">
                    <div
                      className="text-[11px] font-bold"
                      style={{
                        color: toneColor[actorTone[m.actorType] ?? "dim"],
                      }}
                    >
                      {m.actorLabel}
                    </div>
                    <div
                      className="text-[11px]"
                      style={{ color: "var(--text-tertiary)" }}
                    >
                      {relativeTime(m.createdAt)}
                    </div>
                  </div>
                  <div
                    className="min-w-0 flex-1 break-words pl-3 text-[12px] leading-relaxed"
                    style={{
                      borderLeft: `1px solid ${toneColor[actorTone[m.actorType] ?? "dim"]}40`,
                      color: "var(--text-primary)",
                    }}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              {task.discussion.length === 0 ? (
                <div
                  className="text-[12px]"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  Zatím žádná komunikace.
                </div>
              ) : null}
            </div>
            {contributor ? (
              <div
                className="mt-3 flex gap-2 pt-3"
                style={{ borderTop: "1px solid var(--border-subtle)" }}
              >
                <input
                  className={inputClass}
                  placeholder={cs.task.comment}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && comment.trim())
                      commentMutation.mutate();
                  }}
                  data-testid="task-comment-input"
                />
                <Btn
                  disabled={!comment.trim() || commentMutation.isPending}
                  onClick={() => commentMutation.mutate()}
                >
                  →
                </Btn>
              </div>
            ) : null}
          </Panel>
        </div>

        {/* pravý sloupec */}
        <div className="flex flex-col gap-4">
          {contributor && availableMoves.length > 0 ? (
            <Panel className="p-4">
              <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
                {cs.task.moveTo}
              </div>
              <div className="flex flex-wrap gap-2">
                {availableMoves.map((t) => {
                  const target = workflow?.states.find((s) => s.key === t.to);
                  return (
                    <Btn
                      key={t.to}
                      onClick={() => moveMutation.mutate(t.to)}
                      disabled={moveMutation.isPending}
                      data-testid={`task-move-${t.to}`}
                    >
                      {target?.label ?? t.to}
                      {t.label ? ` (${t.label})` : ""}
                    </Btn>
                  );
                })}
              </div>
            </Panel>
          ) : null}

          {task.parentId || task.subtasks.length > 0 ? (
            <Panel tone="amber" className="p-4">
              <div className="flex justify-between">
                <span className="af-label" style={{ color: "var(--warning)" }}>
                  {cs.task.parent}
                  {task.parent
                    ? ` // ${task.parent.title.toUpperCase()}`
                    : task.subtasks.length
                      ? ` // #${task.number}`
                      : ""}
                </span>
                {task.subtasks.length ? (
                  <span className="af-label-dim">
                    {cs.task.subtasks(
                      task.subtasks.length,
                      task.codebases.length,
                    )}
                  </span>
                ) : null}
              </div>
              <div className="mt-2">
                {(task.parent ? [task.parent] : task.subtasks).map((st) => (
                  <Link
                    key={st.id}
                    to="/projects/$slug/tasks/$number"
                    params={{ slug, number: String(st.number) }}
                    className="flex items-center justify-between py-1.5 text-[12px]"
                    style={{
                      borderTop:
                        "1px solid color-mix(in srgb, var(--accent) 5%, transparent)",
                    }}
                  >
                    <span style={{ color: "var(--text-primary)" }}>
                      #{st.number} {st.title}
                    </span>
                    <span
                      className="text-[11px] tracking-[0.08em]"
                      style={{
                        color:
                          st.status === "DONE"
                            ? "var(--positive)"
                            : "var(--text-secondary)",
                      }}
                    >
                      {st.status}
                    </span>
                  </Link>
                ))}
              </div>
              {task.subtasks.length ? (
                <div
                  className="mt-2 text-[11px] leading-relaxed"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  {cs.task.parentQaNote}
                </div>
              ) : null}
            </Panel>
          ) : null}

          <Panel className="p-4" data-testid="task-ep-panel">
            <div className="af-label mb-2.5" style={{ color: "var(--accent)" }}>
              {cs.task.ep} // {task.epScore}
            </div>
            {task.epBreakdown.map((f) => (
              <div
                key={f.key}
                className="mb-1.5 flex justify-between text-[11px]"
              >
                <span style={{ color: "var(--text-secondary)" }}>
                  {f.label}
                </span>
                <span
                  style={{
                    color:
                      f.key === "override" ? "var(--warning)" : "var(--accent)",
                  }}
                >
                  +{f.value}
                </span>
              </div>
            ))}
          </Panel>

          <DependenciesPanel
            task={task}
            slug={slug}
            contributor={contributor}
            onChanged={invalidate}
          />

          <TaskSessionsPanel task={task} />

          <QaPanel
            task={task}
            inGate={state?.kind === "GATE"}
            contributor={contributor}
            onChanged={invalidate}
            onError={onError}
          />

          <GitArtifactsPanel task={task} />

          <PitchScreensPanel task={task} slug={slug} />

          <Panel className="p-4" data-testid="task-trace">
            <div className="af-label mb-2.5" style={{ color: "var(--accent)" }}>
              {cs.task.traceability}
            </div>
            <div className="flex flex-col gap-1 font-mono text-[11px]">
              <TraceRow
                arrow=""
                text={
                  task.goalTitle
                    ? `goal: ${task.goalTitle}`
                    : "goal: — (bez vazby)"
                }
                dim={!task.goalTitle}
              />
              <TraceRow
                arrow="└→"
                text={
                  task.initiativeTitle
                    ? `iniciativa: ${task.initiativeTitle}`
                    : "iniciativa: —"
                }
                dim={!task.initiativeTitle}
              />
              <TraceRow
                arrow="  └→"
                text={
                  task.milestoneTitle
                    ? `milestone: ${task.milestoneTitle}`
                    : "milestone: —"
                }
                dim={!task.milestoneTitle}
              />
              {task.originProposalTitle ? (
                <TraceRow
                  arrow="  └→"
                  text={`návrh: ${task.originProposalTitle}`}
                />
              ) : null}
              <TraceRow
                arrow="    └→"
                text={`task: #${task.number} ${task.title}`}
              />
              <TraceRow
                arrow="      └→"
                text={
                  task.codebases.length
                    ? `codebases: ${task.codebases.map((c) => c.slug).join(", ")}`
                    : "codebases: —"
                }
                dim={!task.codebases.length}
              />
              <TraceRow
                arrow="        └→"
                text={
                  task.releaseVersion
                    ? `release: ${task.releaseVersion}`
                    : "release: —"
                }
                dim={!task.releaseVersion}
              />
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function TaskSessionsPanel({ task }: { task: TaskDetailDTO }) {
  const sessions = useQuery({
    queryKey: ["sessions", "task", task.id],
    queryFn: () => api.sessions({ taskId: task.id, limit: 10 }),
    refetchInterval: 30_000,
  });
  const list = sessions.data?.sessions ?? [];
  const active = list.find((s) => s.status === "ACTIVE") ?? null;

  return (
    <>
      {active ? (
        <Panel tone="green" className="p-4" data-testid="task-live-session">
          <div className="flex justify-between">
            <span className="af-label" style={{ color: "var(--positive)" }}>
              LIVE SESSION
            </span>
            <span
              className="af-animate-pulse text-[11px]"
              style={{ color: "var(--positive)" }}
            >
              ● PRACUJE
            </span>
          </div>
          <div
            className="mt-2 text-[11px] font-bold"
            style={{ color: "var(--text-primary)" }}
          >
            {active.workerName}
          </div>
          <div
            className="mb-2.5 mt-1 text-[11px]"
            style={{ color: "var(--text-secondary)" }}
          >
            {relativeTime(active.startedAt)}
            {sessionCostLabel(active.weekPctSpent, active.tokensTotal)
              ? ` · ${sessionCostLabel(active.weekPctSpent, active.tokensTotal)}`
              : ""}
            {active.models[0] ? ` · ${active.models[0]}` : ""}
          </div>
          <div className="flex items-center gap-2">
            <div
              className="h-[3px] flex-1"
              style={{
                background:
                  "color-mix(in srgb, var(--positive) 15%, transparent)",
              }}
            >
              <div
                className="h-[3px]"
                style={{
                  width: `${active.progress}%`,
                  background: "var(--positive)",
                }}
              />
            </div>
            <span className="text-[11px]" style={{ color: "var(--positive)" }}>
              {active.progress} %
            </span>
          </div>
          {active.currentActivity ? (
            <div
              className="mt-2 font-mono text-[11px]"
              style={{ color: "var(--text-secondary)" }}
            >
              » {active.currentActivity}
            </div>
          ) : null}
          <Link
            to="/sessions/$id"
            params={{ id: active.id }}
            className="mt-2 inline-block text-[11px]"
            style={{ color: "var(--accent)" }}
          >
            DETAIL SESSION →
          </Link>
        </Panel>
      ) : null}

      <Panel className="p-4">
        <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
          {cs.task.artifacts}
        </div>
        {list.length === 0 ? (
          <div
            className="text-[12px]"
            style={{ color: "var(--text-tertiary)" }}
          >
            Zatím žádná session — task čeká na claim workera.
          </div>
        ) : (
          list.map((s) => (
            <Link
              key={s.id}
              to="/sessions/$id"
              params={{ id: s.id }}
              className="mb-1.5 flex items-center justify-between gap-2 text-[11px]"
            >
              <span
                className="min-w-0 truncate"
                style={{ color: "var(--text-secondary)" }}
              >
                #{s.id.slice(-5)} · {s.workerName} · {relativeTime(s.startedAt)}
              </span>
              <span
                className="flex-none"
                style={{
                  color:
                    s.status === "COMPLETED"
                      ? "var(--positive)"
                      : s.status === "ACTIVE"
                        ? "var(--accent)"
                        : s.status === "PAUSED"
                          ? "var(--warning)"
                          : "var(--critical)",
                }}
              >
                {s.status}
              </span>
            </Link>
          ))
        )}
      </Panel>
    </>
  );
}

/** QA panel (spec §53): DoD checklist ↔ test runs, verdikt, rework loop. */
function QaPanel({
  task,
  inGate,
  contributor,
  onChanged,
  onError,
}: {
  task: TaskDetailDTO;
  inGate: boolean;
  contributor: boolean;
  onChanged: () => void;
  onError: (err: unknown) => void;
}) {
  const [note, setNote] = useState("");
  const verdictMutation = useMutation({
    mutationFn: (verdict: "PASSED" | "CHANGES_REQUESTED") =>
      api.qaVerdict(task.id, { verdict, note }),
    onSuccess: () => {
      setNote("");
      onChanged();
    },
    onError,
  });

  // poslední test run per DoD check (párování přes checkKey)
  const latestByCheck = new Map<string, TaskDetailDTO["testRuns"][number]>();
  for (const run of task.testRuns) {
    if (run.checkKey && !latestByCheck.has(run.checkKey))
      latestByCheck.set(run.checkKey, run);
  }
  const acceptanceMet =
    task.acceptance.length > 0 && task.acceptance.every((a) => a.met);

  const tone =
    task.qaVerdict === "PASSED"
      ? "green"
      : task.qaVerdict === "CHANGES_REQUESTED"
        ? "red"
        : inGate
          ? "amber"
          : undefined;

  return (
    <Panel
      tone={tone === "green" ? "green" : tone === "amber" ? "amber" : undefined}
      className="p-4"
      data-testid="task-qa-panel"
    >
      <div className="flex items-center justify-between">
        <span
          className="af-label"
          style={{ color: tone ? toneColor[tone as Tone] : "var(--accent)" }}
        >
          {cs.task.qa}
        </span>
        {task.reworkCount > 0 ? (
          <span
            className="px-2 py-0.5 text-[11px] tracking-[0.1em]"
            style={{
              borderRadius: "var(--radius-box)",
              color: "var(--critical)",
              border:
                "1px solid color-mix(in srgb, var(--critical) 40%, transparent)",
            }}
            data-testid="task-rework-badge"
          >
            {cs.task.qaRework(task.reworkCount)}
          </span>
        ) : null}
      </div>

      {/* DoD checklist */}
      <div className="mt-2.5 flex flex-col gap-1.5" data-testid="task-dod">
        {task.dodChecks.map((check) => {
          const run =
            check.key === "acceptance" && !latestByCheck.has("acceptance")
              ? null
              : (latestByCheck.get(check.key) ?? null);
          // SKIPPED (např. screens u tasku bez UI) je neutrální stav, ne selhání
          const ok =
            check.key === "acceptance" && !run
              ? acceptanceMet
                ? true
                : null
              : run
                ? run.status === "SKIPPED"
                  ? null
                  : run.status === "PASSED"
                : null;
          return (
            <div
              key={check.key}
              className="flex items-start justify-between gap-2 text-[12px]"
            >
              <span className="flex min-w-0 gap-2">
                <span
                  style={{
                    color:
                      ok === true
                        ? "var(--positive)"
                        : ok === false
                          ? "var(--critical)"
                          : "var(--text-tertiary)",
                  }}
                >
                  {ok === true ? "✓" : ok === false ? "✗" : "□"}
                </span>
                <span style={{ color: "var(--text-primary)" }}>
                  {check.label}
                  {check.command ? (
                    <span
                      className="ml-1.5 font-mono text-[11px]"
                      style={{ color: "var(--text-tertiary)" }}
                    >
                      {check.command}
                    </span>
                  ) : null}
                </span>
              </span>
              {run?.statsTotal != null ? (
                <span
                  className="shrink-0 font-mono text-[11px]"
                  style={{
                    color:
                      run.status === "PASSED"
                        ? "var(--positive)"
                        : "var(--critical)",
                  }}
                >
                  {run.statsPassed}/{run.statsTotal}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* verdikt / poslední verdikt */}
      {task.qaVerdict ? (
        <div
          className="mt-3 pt-2.5"
          style={{ borderTop: "1px solid var(--border-subtle)" }}
          data-testid="task-qa-verdict"
        >
          <span
            className="text-[12px] font-bold tracking-[0.08em]"
            style={{
              color:
                task.qaVerdict === "PASSED"
                  ? "var(--positive)"
                  : "var(--critical)",
            }}
          >
            {task.qaVerdict === "PASSED" ? cs.task.qaPassed : cs.task.qaChanges}
          </span>
          {task.qaNote ? (
            <div
              className="mt-1 text-[11px] leading-relaxed"
              style={{ color: "var(--text-secondary)" }}
            >
              {task.qaNote}
            </div>
          ) : null}
          {task.qaByLabel && task.qaAt ? (
            <div
              className="mt-1 text-[11px]"
              style={{ color: "var(--text-tertiary)" }}
            >
              {cs.task.qaBy(task.qaByLabel, relativeTime(task.qaAt))}
            </div>
          ) : null}
        </div>
      ) : null}

      {inGate && contributor ? (
        <div
          className="mt-3 pt-2.5"
          style={{ borderTop: "1px solid var(--border-subtle)" }}
        >
          <div className="mb-2 text-[11px]" style={{ color: "var(--warning)" }}>
            {cs.task.qaAwaiting}
          </div>
          <input
            className={inputClass}
            placeholder={cs.task.qaNotePlaceholder}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="task-qa-note"
          />
          <div className="mt-2 flex gap-2">
            <Btn
              variant="secondary"
              tone="green"
              className="flex-1"
              disabled={verdictMutation.isPending}
              onClick={() => verdictMutation.mutate("PASSED")}
              data-testid="task-qa-pass"
            >
              {cs.task.qaPassed}
            </Btn>
            <Btn
              variant="secondary"
              tone="red"
              className="flex-1"
              disabled={verdictMutation.isPending}
              onClick={() => verdictMutation.mutate("CHANGES_REQUESTED")}
              data-testid="task-qa-return"
            >
              {cs.task.qaChanges}
            </Btn>
          </div>
        </div>
      ) : null}

      {/* test runs historie */}
      {task.testRuns.length > 0 ? (
        <div
          className="mt-3 pt-2.5"
          style={{ borderTop: "1px solid var(--border-subtle)" }}
        >
          <div className="af-label-dim mb-1.5">{cs.task.testRuns}</div>
          {task.testRuns.slice(0, 6).map((run) => (
            <div
              key={run.id}
              className="mb-1 flex items-center justify-between gap-2 font-mono text-[11px]"
            >
              <span
                className="min-w-0 truncate"
                style={{ color: "var(--text-secondary)" }}
              >
                {run.name}
                {run.codebase ? ` · ${run.codebase}` : ""}
              </span>
              <span
                className="flex-none"
                style={{
                  color:
                    run.status === "PASSED"
                      ? "var(--positive)"
                      : run.status === "FAILED"
                        ? "var(--critical)"
                        : "var(--text-secondary)",
                }}
              >
                {run.statsTotal != null
                  ? `${run.statsPassed}/${run.statsTotal} `
                  : ""}
                {run.status}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div
          className="mt-2.5 text-[11px]"
          style={{ color: "var(--text-tertiary)" }}
        >
          {cs.task.qaNoRuns}
        </div>
      )}
    </Panel>
  );
}

/** Git artefakty napříč sessions tasku (spec §54 — traceability k branch/commit/PR). */
function GitArtifactsPanel({ task }: { task: TaskDetailDTO }) {
  const icon: Record<string, string> = {
    BRANCH: "⎇",
    COMMIT: "●",
    PR: "⇋",
    TEST: "✓",
    FILE: "▤",
    OTHER: "▪",
  };
  return (
    <Panel className="p-4" data-testid="task-git-artifacts">
      <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
        {cs.task.gitArtifacts}
      </div>
      {task.gitArtifacts.length === 0 ? (
        <div className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
          {cs.task.gitArtifactsEmpty}
        </div>
      ) : (
        task.gitArtifacts.map((a) => (
          <div
            key={a.id}
            className="mb-1.5 flex items-center justify-between font-mono text-[11px]"
          >
            <span className="flex min-w-0 gap-2">
              <span style={{ color: "var(--accent)" }}>
                {icon[a.kind] ?? "▪"}
              </span>
              <span
                className="truncate"
                style={{ color: "var(--text-primary)" }}
              >
                {a.label}
              </span>
            </span>
            <span
              className="ml-2 shrink-0 text-[11px]"
              style={{ color: "var(--text-tertiary)" }}
            >
              {a.kind} · {relativeTime(a.createdAt)}
            </span>
          </div>
        ))
      )}
    </Panel>
  );
}

/** Pitch Perfect obrazovky, jejichž screenshoty vznikly v sessions tasku. */
function PitchScreensPanel({
  task,
  slug,
}: {
  task: TaskDetailDTO;
  slug: string;
}) {
  if (task.screens.length === 0) return null;
  const dot: Record<string, string> = {
    APPROVED: "var(--positive)",
    PENDING: "var(--warning)",
    CHANGES_REQUESTED: "var(--critical)",
  };
  return (
    <Panel className="p-4" data-testid="task-pitch-screens">
      <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
        {cs.task.pitchScreens}
      </div>
      {task.screens.map((s) => (
        <div
          key={s.id}
          className="mb-1.5 flex items-center justify-between font-mono text-[11px]"
        >
          <span className="flex min-w-0 gap-2">
            <span style={{ color: dot[s.status] ?? "var(--text-secondary)" }}>
              ●
            </span>
            <span className="truncate" style={{ color: "var(--text-primary)" }}>
              {s.title}
            </span>
          </span>
          <span
            className="ml-2 shrink-0 text-[11px]"
            style={{ color: "var(--text-tertiary)" }}
          >
            {s.key}
          </span>
        </div>
      ))}
      <Link
        to="/projects/$slug/$tab"
        params={{ slug, tab: "pitch" }}
        className="mt-1 inline-block text-[11px] tracking-[0.1em] hover:underline"
        style={{ color: "var(--accent)" }}
        data-testid="task-open-pitch"
      >
        {cs.task.openPitch}
      </Link>
    </Panel>
  );
}

function MetaChip({ label }: { label: string }) {
  return (
    <span
      className="px-2 py-0.5 text-[11px]"
      style={{
        borderRadius: "var(--radius-box)",
        color: "var(--accent)",
        border: "1px solid color-mix(in srgb, var(--accent) 20%, transparent)",
      }}
    >
      {label}
    </span>
  );
}

function TraceRow({
  arrow,
  text,
  dim,
}: {
  arrow: string;
  text: string;
  dim?: boolean;
}) {
  return (
    <div className="flex gap-2">
      <span className="whitespace-pre" style={{ color: "var(--accent)" }}>
        {arrow}
      </span>
      <span
        className="min-w-0 break-words"
        style={{
          color: dim ? "var(--text-tertiary)" : "var(--text-secondary)",
        }}
      >
        {text}
      </span>
    </div>
  );
}

function DependenciesPanel({
  task,
  slug,
  contributor,
  onChanged,
}: {
  task: TaskDetailDTO;
  slug: string;
  contributor: boolean;
  onChanged: () => void;
}) {
  /**
   * Výběr závislosti hledá, nestahuje. Dřív si panel načetl **všechny tasky
   * projektu** i s osmi relacemi jen kvůli jednomu selectu — na desce se
   * stovkami tasků to byl týž problém jako u kanbanu.
   */
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState("");
  const tasks = useQuery({
    queryKey: ["section", task.projectId, "tasks", "search", search],
    queryFn: () =>
      api.tasks(task.projectId, { q: search || undefined, limit: 20 }),
    enabled: search.trim().length > 0,
  });
  const addMutation = useMutation({
    mutationFn: () =>
      api.addTaskDependency(task.id, { dependsOnTaskId: selected }),
    onSuccess: () => {
      setSelected("");
      onChanged();
    },
  });
  const removeMutation = useMutation({
    mutationFn: (toId: string) => api.removeTaskDependency(task.id, toId),
    onSuccess: onChanged,
  });

  const candidates = (tasks.data?.tasks ?? []).filter(
    (t) => t.id !== task.id && !task.dependsOn.some((d) => d.id === t.id),
  );

  return (
    <Panel className="p-4" data-testid="task-deps">
      <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
        {cs.task.dependencies}
      </div>
      {task.dependsOn.length === 0 ? (
        <div className="text-[12px]" style={{ color: "var(--text-tertiary)" }}>
          {cs.task.noDeps}
        </div>
      ) : (
        task.dependsOn.map((d) => (
          <div
            key={d.id}
            className="mb-1.5 flex items-center justify-between gap-2 text-[12px]"
          >
            <Link
              to="/projects/$slug/tasks/$number"
              params={{ slug, number: String(d.number) }}
              className="min-w-0 break-words"
              style={{ color: "var(--text-primary)" }}
            >
              #{d.number} {d.title}
            </Link>
            <span className="flex flex-none items-center gap-2">
              <span
                className="text-[11px] tracking-[0.08em]"
                style={{
                  color: d.done ? "var(--positive)" : "var(--critical)",
                }}
              >
                {d.done ? "DONE" : d.status}
              </span>
              {contributor ? (
                <button
                  type="button"
                  className="cursor-pointer text-[11px]"
                  style={{ color: "var(--critical)" }}
                  onClick={() => removeMutation.mutate(d.id)}
                >
                  ✕
                </button>
              ) : null}
            </span>
          </div>
        ))
      )}
      {task.dependedBy.length > 0 ? (
        <div
          className="mt-2 text-[11px]"
          style={{ color: "var(--text-tertiary)" }}
        >
          Blokuje: {task.dependedBy.map((d) => `#${d.number}`).join(", ")}
        </div>
      ) : null}
      {contributor ? (
        <div
          className="mt-2 pt-2"
          style={{ borderTop: "1px solid var(--border-subtle)" }}
        >
          <div className="flex items-end gap-2">
            <input
              className={inputClass}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSelected("");
              }}
              placeholder={`${cs.task.addDependency}… (název nebo #číslo)`}
              data-testid="task-dep-search"
            />
            <Btn
              disabled={!selected || addMutation.isPending}
              onClick={() => addMutation.mutate()}
            >
              +
            </Btn>
          </div>
          {search.trim() ? (
            <div className="mt-2 flex flex-col gap-1">
              {candidates.slice(0, 8).map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setSelected(t.id)}
                  className="px-3 py-2 text-left text-[12px]"
                  style={{
                    borderRadius: "var(--radius-chip)",
                    border: `1px solid ${selected === t.id ? "var(--accent-border)" : "var(--border-default)"}`,
                    background:
                      selected === t.id ? "var(--accent-bg)" : "transparent",
                    color: "var(--text-primary)",
                  }}
                  data-testid={`task-dep-option-${t.number}`}
                >
                  #{t.number} {t.title}
                </button>
              ))}
              {candidates.length === 0 && !tasks.isFetching ? (
                <span
                  className="text-[12px]"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  {cs.task.noDependencyMatch}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </Panel>
  );
}

/**
 * Nahrání obrázkové přílohy tasku: výběr souboru, drag&drop nebo vložení
 * screenshotu ze schránky (Cmd+V). Vložení ze schránky je tu proto, že přesně
 * takhle screenshot vzniká — jinak by ho člověk musel nejdřív ukládat na disk.
 */
function AttachmentDrop({
  busy,
  full,
  onFile,
}: {
  busy: boolean;
  full: boolean;
  onFile: (file: File) => void;
}) {
  const [over, setOver] = useState(false);

  if (full) {
    return (
      <div
        className="mt-2 text-[11px]"
        style={{ color: "var(--text-tertiary)" }}
      >
        {cs.task.attachmentsFull}
      </div>
    );
  }

  const takeFirstImage = (files: FileList | File[] | null | undefined) => {
    const image = Array.from(files ?? []).find((f) =>
      f.type.startsWith("image/"),
    );
    if (image) onFile(image);
  };

  return (
    <label
      className="mt-3 flex cursor-pointer items-center justify-center px-3 py-2.5 text-[11px] tracking-[0.12em]"
      style={{
        border: `1px dashed ${over ? "var(--accent)" : "var(--border-subtle)"}`,
        color: over ? "var(--accent)" : "var(--text-tertiary)",
        background: over
          ? "color-mix(in srgb, var(--accent) 4%, transparent)"
          : "transparent",
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        takeFirstImage(e.dataTransfer.files);
      }}
      onPaste={(e) => takeFirstImage(e.clipboardData.files)}
      // pozor: NE prefix „task-attachment-", ten už adresuje jednotlivé přílohy
      data-testid="task-screenshot-drop"
    >
      <input
        type="file"
        accept="image/*"
        className="hidden"
        disabled={busy}
        onChange={(e) => {
          takeFirstImage(e.target.files);
          e.target.value = "";
        }}
      />
      {busy ? cs.task.attachmentUploading : cs.task.attachmentDrop}
    </label>
  );
}
